<template>
  <b-modal
    v-model="editorShown"
    title="New Recurrent Documents"
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
    size="md"
    centered
    no-close-on-backdrop
    @ok.prevent="submitForm"
  >
    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel">
        Cancel
      </b-button>
      <b-button variant="primary" @click="ok">
        Send
      </b-button>
    </template>
    <b-row>
      <b-col>
        <b-form-group
          label="Account"
          :state="formErrors.portalAccount.length == 0 ? null : false"
          :invalid-feedback="formErrors.portalAccount.join(', ')"
        >
          <v-select
            v-model="form.portalAccount"
            :options="portalAccounts"
          ></v-select>
        </b-form-group>
        <b-form-group
          label="Form"
          :state="formErrors.formId.length == 0 ? null : false"
          :invalid-feedback="formErrors.formId.join(', ')"
        >
          <v-select
            v-model="form.formId"
            :options="allFormsComputed"
          ></v-select>
        </b-form-group>
        <b-form-group>
          <recurrence-control
            :startDate="startDateTime"
            v-model="form.recurrence"
            :formErrors="formErrors.recurrence"
            :maxOccurrences="50"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import portalAccessServer from "../../../server/portalAccess";
import portalDocumentServer from "../../../server/portalDocument";
import { validateRequired } from "../../../helpers/validation";
import RecurrenceControl from "../../controls/RecurrenceControl.vue";

export default {
  name: "ShareFormRecurrentModal",
  components: { RecurrenceControl },
  props: ["eocId", "patientId"],
  data() {
    return {
      portalAccounts: [],
      form: {
        portalAccount: [],
        formId: null,
        recurrence: {
          frequencyNumber: 1,
          frequencyUnit: "Days",
          monthlyRepeat: null,
          endType: "ByOccurrences",
          endDate: null,
          occurrences: 10,
          sunday: false,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false
        }
      },
      formErrors: {
        portalAccount: [],
        formId: [],
        recurrence: {
          ends: [],
          weekDay: []
        }
      }
    };
  },
  mounted() {
    this.fetchPortalAccounts();
    this.fetchSharePackages();
    this.fetchFormBuilderForms({ category: "main" });
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", ["fetchSharePackages"]),
    ...mapActions("formBuilder", ["fetchFormBuilderForms"]),
    async fetchPortalAccounts() {
      const portalAccounts = await portalAccessServer.findAllAccess(
        this.patientId
      );
      this.portalAccounts = portalAccounts.map(access => ({
        value: access.id,
        label: `${access.name} (${access.relationship})`
      }));
      if (this.portalAccounts.length == 1) {
        this.form.portalAccount = this.portalAccounts[0];
      }
    },
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      const formData = {
        portalAccessId: this.form.portalAccount.value,
        patientId: this.patientId,
        episodeOfCareId: this.eocId,
        formId: this.form.formId.value,
        recurrence: {
          frequencyNumber: this.form.recurrence.frequencyNumber,
          frequencyUnit: this.form.recurrence.frequencyUnit,
          endType: this.form.recurrence.endType,
          ...(this.form.recurrence.endType == "ByDate" && {
            endDate: this.formatDateTimeSql(this.form.recurrence.endDate)
          }),
          ...(this.form.recurrence.endType == "ByOccurrences" && {
            occurrences: this.form.recurrence.occurrences
          }),
          ...(this.form.recurrence.frequencyUnit == "Months" && {
            monthlyRepeat: this.form.recurrence.monthlyRepeat
          }),
          ...(this.form.recurrence.frequencyUnit == "Weeks" && {
            sunday: this.form.recurrence.sunday,
            monday: this.form.recurrence.monday,
            tuesday: this.form.recurrence.tuesday,
            wednesday: this.form.recurrence.wednesday,
            thursday: this.form.recurrence.thursday,
            friday: this.form.recurrence.friday,
            saturday: this.form.recurrence.saturday
          })
        }
      };

      await portalDocumentServer.shareRecurrentForm(formData);

      this.$emit("onModify");
    },
    validateForm() {
      let withoutErrors = true;

      this.formErrors.portalAccount = [];
      if (!validateRequired(this.form.portalAccount)) {
        this.formErrors.portalAccount.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.formId = [];
      if (!validateRequired(this.form.formId)) {
        this.formErrors.formId.push("This field is required");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    getDaysIntervalId(intervalLabel, intervalAmount) {
      switch (`${intervalAmount}${intervalLabel}`) {
        case "2Days":
          return "every2Days";
        case "3Days":
          return "every3Days";
        case "1Weeks":
          return "1timePerWeek";
        case "2Weeks":
          return "everyTwoWeeks";
        case "3Weeks":
          return "every3Weeks";
        case "28Days":
          return "every28Days";
        case "30Days":
          return "every30Days";

        default:
          return null;
      }
    },
    toIntervalAmounts() {
      switch (this.form.daysIntervalId) {
        case "every2Days":
          return {
            frequency: "Days",
            amount: 2
          };
        case "every3Days":
          return {
            frequency: "Days",
            amount: 3
          };
        case "1timePerWeek":
          return {
            frequency: "Weeks",
            amount: 1
          };
        case "everyTwoWeeks":
          return {
            frequency: "Weeks",
            amount: 2
          };
        case "every3Weeks":
          return {
            frequency: "Weeks",
            amount: 3
          };
        case "every28Days":
          return {
            frequency: "Days",
            amount: 28
          };
        case "every30Days":
          return {
            frequency: "Days",
            amount: 30
          };

        default:
          return {
            frequency: "Days",
            amount: 1
          };
      }
    }
  },
  computed: {
    ...mapState("client", {
      sharePackages: state =>
        state.sharePackages.map(s => ({
          value: s.id,
          label: s.name,
          data: s
        }))
    }),
    ...mapState("formBuilder", {
      allForms: state => state.list
    }),
    ...mapGetters("client", ["formGroupsCurrentLocation"]),
    ...mapGetters("layout", ["hasCurrentUserDocumentPermission"]),
    allFormsComputed() {
      let forms = this.formOptions.filter(f =>
        this.hasCurrentUserDocumentPermission(f.value, "isReadOnly")
      );
      let formGroups = this.formGroups.filter(fg =>
        this.hasCurrentUserDocumentPermission(fg.value, "isReadOnly")
      );
      return formGroups
        .concat(forms)
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    formOptions() {
      return this.allForms["main"].map(form => ({
        value: form.id,
        label: form.name
      }));
    },
    formGroups() {
      return this.formGroupsCurrentLocation.map(form => ({
        value: form.id,
        label: form.name
      }));
    },
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    startDateTime() {
      return this.formatDateTimeSql(this.getCurrentAdjustedDateTime());
    }
  }
};
</script>

<style></style>
